import { closeInPageNotifications, type NotifyPayload } from "../../../lib/notifications";
import { setLocalUser } from "../../../lib/socket";
import type { NotifyArgs } from "../../notifications/useInPageNotification";

// Module copy of ChatLayout's pure key helper (same pattern as useFavorites / useAndroidShare).
const fq = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);

type Updater<T> = T | ((prev: T) => T);

type Options<Member extends { id: string; status?: string }> = {
  socket: { emit: (event: string, payload?: unknown) => unknown };
  /** The signed-in user's id (empty before sign-in). */
  userId?: string;
  currentVoidId: string;
  currentChannelId: string;
  /** The open space, read at call time. */
  currentVoidIdRef: { current: string };
  channels: { id: string; name?: string }[];
  mutedSpaces: Record<string, boolean>;
  hiddenSpaces: Record<string, boolean>;
  favorites: string[];
  setMe: (user: { userId: string; name: string; avatarUrl: string | null }) => void;
  setUnread: (value: Updater<Record<string, number>>) => void;
  setNotified: (value: Updater<Record<string, boolean>>) => void;
  setMembers: (updater: (prev: Member[]) => Member[]) => void;
  handleNotification: (args: NotifyArgs) => void;
  addPreview: NotifyArgs["addPreview"];
  refreshPreview: NotifyArgs["refreshPreview"];
  markUnread: NotifyArgs["markUnread"];
  openChannelFromFqid: (fqid: string, fallbackVoidId?: string) => void;
  notifiedRef: NotifyArgs["notifiedRef"];
  seenIdsRef: NotifyArgs["seenIdsRef"];
};

/**
 * The small socket handlers about the signed-in user and alerts: who we are, the channel list changing
 * under us, unread badges cleared elsewhere, notifications for other spaces, a member's status, and
 * join-request changes.
 *
 * Returns the handlers, built fresh each render (so each sees the current state); ChatLayout registers
 * them with the rest of the socket events. Moved out of ChatLayout with the same behaviour.
 */
export function useAccountSocketHandlers<Member extends { id: string; status?: string }>({
  socket,
  userId,
  currentVoidId,
  currentChannelId,
  currentVoidIdRef,
  channels,
  mutedSpaces,
  hiddenSpaces,
  favorites,
  setMe,
  setUnread,
  setNotified,
  setMembers,
  handleNotification,
  addPreview,
  refreshPreview,
  markUnread,
  openChannelFromFqid,
  notifiedRef,
  seenIdsRef,
}: Options<Member>) {
  const onAuthAccepted = ({ userId: id, name, avatarUrl }: { userId: string; name: string; avatarUrl?: string | null }) => {
    const merged = { userId: id, name, avatarUrl: avatarUrl ?? null };
    setMe(merged);
    setLocalUser(merged);
  };

  const onChannelsReordered = ({ spaceId }: { spaceId: string }) => {
    const sid = String(spaceId || "");
    if (!sid || !socket) return;
    if (sid === currentVoidIdRef.current) {
      socket.emit("channel:list", { voidId: sid });
    }
  };

  // The same user read this channel on another device/tab — dismiss its
  // notification here (service-worker push notifications + page-shown ones) and
  // drop the in-app unread badge so every surface agrees it's been read.
  const onNotificationClear = ({ channelId }: { channelId: string; spaceId?: string }) => {
    if (!channelId) return;
    try { navigator.serviceWorker?.controller?.postMessage({ type: 'CLEAR_CHANNEL_NOTIFICATIONS', data: { channelId } }); } catch { /* noop */ }
    try { closeInPageNotifications(channelId); } catch { /* noop */ }
    setUnread((prev) => {
      if (!prev[channelId]) return prev;
      const next = { ...prev };
      delete next[channelId];
      return next;
    });
  };

  // The user hit "mark all as read" (here or on another device) — wipe all local
  // unread/notified state so every badge clears at once.
  const onUnreadReset = () => {
    setUnread({});
    setNotified({});
  };

  const onJoinRequestsChanged = () => {
    try { socket.emit('void:list'); } catch {}
  };

  const onSpaceNotify = ({ voidId, channelId, authorId, authorName, content, messageId, pushSent }: NotifyPayload) => {
    const fid = fq(voidId, channelId);
    const isMineMsg = !!(authorId && userId && authorId === userId);
    const channelName = channels.find((c) => fq(voidId, c.id) === fid)?.name;
    handleNotification({
      voidId,
      channelId,
      channelName,
      authorId,
      authorName,
      content,
      messageId,
      pushSent,
      isMine: isMineMsg,
      isCurrentChannel: voidId === currentVoidId && fid === fq(currentVoidId, currentChannelId),
      mutedSpaces,
      hiddenSpaces,
      favorites,
      addPreview,
      refreshPreview,
      markUnread,
      onClickChannel: (vid, cid) => {
        const target = cid.includes(':') ? cid : fq(vid, cid);
        openChannelFromFqid(target, vid);
      },
      notifiedRef,
      seenIdsRef,
      setNotified: (fid: string) => setNotified((prev) => ({ ...prev, [fid]: true })),
    });
  };

  const onUserStatus = ({ userId: id, status }: { userId: string; status: string }) => {
    setMembers(prev => prev.map(m => m.id === id ? { ...m, status } : m));
    // If it's me, reflect in local snapshot too
    if (userId && id === userId) {
      try {
        const raw = localStorage.getItem('user');
        const prev = raw ? JSON.parse(raw) : {};
        localStorage.setItem('user', JSON.stringify({ ...prev, status }));
      } catch { /* noop */ }
    }
  };

  return { onAuthAccepted, onChannelsReordered, onNotificationClear, onUnreadReset, onJoinRequestsChanged, onSpaceNotify, onUserStatus };
}

